import React, { useEffect, useState, useContext } from "react";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import companyBasedRequest from "../../../request/companyBasedRequest";
import "./staffServicesModal.css";
import { Home, Monitor, Check } from "lucide-react";

const StaffServicesModal = ({ isOpen, onClose, shopId, shopServices = [], staffServices = [], onSave }) => {
	const { language, userData, setUserData } = useContext(CombinedContext);
	const [rows, setRows] = useState([]);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!isOpen) return;
		if (!shopServices || shopServices.length === 0) return;

		const staffSet = new Set(staffServices);

		const mapped = shopServices.map((svc) => ({
			serviceId: svc.serviceId,
			name: svc.name,
			price: svc.price,
			currency: svc.currency,
			duration: svc.duration,
			isHomeService: svc.isHomeService,
			locationType: svc.locationType || (svc.isHomeService ? "home" : "in_shop"),
			active: staffSet.has(svc.serviceId),
		}));

		setRows(mapped);
	}, [isOpen, shopServices, staffServices]);

	useEffect(() => {
		if (!isOpen) setRows([]);
	}, [isOpen]);

	const toggleRow = (index) => {
		setRows((prev) => {
			const next = [...prev];
			next[index] = { ...next[index], active: !next[index].active };
			return next;
		});
	};

	const selectedCount = rows.filter((r) => r.active).length;

	const handleSave = async () => {
		const serviceIds = rows.filter((r) => r.active).map((r) => r.serviceId);
		try {
			setSaving(true);
			const res = await companyBasedRequest.updateUserServices(
				userData.user.userId,
				serviceIds,
				userData.cognitoTokens.AccessToken
			);
			setUserData((prev) => ({ ...prev, user: res.user }));
			onSave?.(serviceIds);
			onClose();
		} catch (err) {
			console.error("Failed to save staff services", err);
		} finally {
			setSaving(false);
		}
	};

	if (!isOpen) return null;

	const t = translations[language];

	const locationBadge = (row) => {
		if (row.locationType === "home") {
			return (
				<span className="sf-staff-svc-badge sf-staff-svc-badge--home">
					<Home size={12} strokeWidth={1.9} />
					{language === "es" ? "A domicilio" : "Home service"}
				</span>
			);
		}
		if (row.locationType === "online") {
			return (
				<span className="sf-staff-svc-badge sf-staff-svc-badge--online">
					<Monitor size={12} strokeWidth={1.9} />
					{language === "es" ? "En línea" : "Online"}
				</span>
			);
		}
		return null;
	};

	return (
		<div className="sf-staff-svc-overlay" onClick={onClose}>
			<div className="sf-staff-svc-modal" onClick={(e) => e.stopPropagation()}>
				{/* HEADER */}
				<div className="sf-staff-svc-header">
					<div className="sf-staff-svc-header-text">
						<h2 className="sf-staff-svc-title">{t?.profileStaff?.services?.title || "My Services"}</h2>
						<span className="sf-staff-svc-subtitle">
							{language === "es"
								? `${selectedCount} de ${rows.length} seleccionados`
								: `${selectedCount} of ${rows.length} selected`}
						</span>
					</div>
					<button className="sf-staff-svc-close" onClick={onClose} aria-label="Close">✕</button>
				</div>

				{/* BODY */}
				<div className="sf-staff-svc-body">
					{rows.length === 0 ? (
						<div className="sf-staff-svc-empty">
							{language === "es" ? "No hay servicios disponibles" : "No services available"}
						</div>
					) : (
						rows.map((row, idx) => (
							<button
								key={row.serviceId}
								type="button"
								className={`sf-staff-svc-card ${row.active ? "is-active" : ""}`}
								onClick={() => toggleRow(idx)}
							>
								<span className="sf-staff-svc-check" aria-hidden="true">
									{row.active && <Check size={14} strokeWidth={2.6} />}
								</span>

								<div className="sf-staff-svc-info">
									<div className="sf-staff-svc-name">{row.name}</div>
									<div className="sf-staff-svc-meta">
										{locationBadge(row)}
									</div>
								</div>

								<div className="sf-staff-svc-pricing">
									<span className="sf-staff-svc-price">{row.currency || "DOP"} {row.price ?? 0}</span>
									<span className="sf-staff-svc-duration">{row.duration} min</span>
								</div>
							</button>
						))
					)}
				</div>

				{/* FOOTER */}
				<div className="sf-base-footer">
					<button className="btn ghost" onClick={onClose}>
						{t.calendar.editor.cancel}
					</button>
					<button className="btn" onClick={handleSave} disabled={saving}>
						{saving ? t.calendar.editor.saving : t.calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default StaffServicesModal;